import { z } from 'zod';
/**
 * Ai được đổi trạng thái task trong một dự án — APM-TSP, người dùng chốt
 * 2026-09-26 (docs/02-sprint-plan.md, mục "Quyền đổi trạng thái task").
 *
 * Hai rule, áp **chỉ** cho việc đổi trạng thái (kéo giữa các cột, menu Đổi cột,
 * nút Bắt đầu/Hoàn thành). Sửa nội dung task vẫn theo `task:update` như cũ.
 *
 * Hàm thuần, web và API cùng dùng: web để biết thẻ nào kéo được, API để chặn —
 * một chỗ viết, không thể lệch nhau.
 */
/** Ai đổi được trạng thái (ngoài Done). */
export const TaskStatusChangePolicy = z.enum(['members', 'assignee_or_creator', 'roles']);
/**
 * Ai chuyển được task vào / ra khỏi cột Done.
 *
 * `not_assignee_and_pm` — **người khác** người được giao (APM-ACHEAT-f, người
 * dùng chốt 2026-10-06): nghiệm thu bởi người thứ hai ở dạng rẻ nhất. Bất kỳ
 * thành viên nào kéo được việc ấy đều đóng được, trừ chính người làm; PM luôn
 * được. Việc chưa giao ai thì ai cũng đóng được — không có "chính mình" để loại.
 */
export const TaskDonePolicy = z.enum([
    'pm',
    'roles_and_pm',
    'assignee_or_creator_and_pm',
    'not_assignee_and_pm',
    'anyone',
]);
export const TaskStatusRules = z.object({
    statusChange: TaskStatusChangePolicy,
    /** Chỉ có nghĩa với `statusChange = 'roles'`. Id của `project_role_definition`. */
    statusChangeRoleIds: z.array(z.uuid()).max(50),
    done: TaskDonePolicy,
    /** Chỉ có nghĩa với `done = 'roles_and_pm'`. */
    doneRoleIds: z.array(z.uuid()).max(50),
});
/**
 * Mặc định do người dùng chốt: mọi thành viên (trừ Chỉ xem) kéo được giữa các
 * cột chưa xong; còn **đóng** (và mở lại) thì chỉ người được giao, người tạo và PM.
 *
 * Trước APM-ACHEAT-b (2026-10-06) rule Done mặc định là `anyone`: ai kéo được
 * thì đóng được, tức bất kỳ thành viên nào cũng đóng được việc của người khác —
 * và việc "xong" là đầu vào của velocity, đúng hạn và làm lại. Dự án muốn mở
 * rộng vẫn chọn lại `anyone` được; đây chỉ là điểm xuất phát.
 */
export const DEFAULT_TASK_STATUS_RULES = {
    statusChange: 'members',
    statusChangeRoleIds: [],
    done: 'assignee_or_creator_and_pm',
    doneRoleIds: [],
};
const same = (a, b) => a !== null && b !== null && a.toLowerCase() === b.toLowerCase();
function isAssigneeOrCreator(actor, task) {
    return same(actor.userId, task.assigneeId) || same(actor.userId, task.createdBy);
}
function hasRole(actor, roleIds) {
    return roleIds.some((id) => same(id, actor.roleId));
}
/** Rule thứ nhất: được kéo task này giữa các cột (không tính Done). */
export function canMoveTask(rules, actor, task) {
    if (actor.isManager)
        return true;
    // Chỉ xem không bao giờ đổi được gì — người dùng chốt 2026-09-26.
    if (!actor.isMember || actor.isViewer)
        return false;
    switch (rules.statusChange) {
        case 'members':
            return true;
        case 'assignee_or_creator':
            return isAssigneeOrCreator(actor, task);
        case 'roles':
            return hasRole(actor, rules.statusChangeRoleIds);
    }
}
/**
 * Được chuyển task này vào / ra khỏi Done.
 *
 * Phải qua **cả hai** rule (người dùng chốt): rule Done chỉ thu hẹp thêm, không
 * mở rộng — `anyone` nghĩa là "không thêm hạn chế nào", không phải "kể cả người
 * không kéo được task".
 */
export function canCompleteTask(rules, actor, task) {
    if (actor.isManager)
        return true;
    if (!canMoveTask(rules, actor, task))
        return false;
    switch (rules.done) {
        case 'pm':
            return false;
        case 'roles_and_pm':
            return hasRole(actor, rules.doneRoleIds);
        case 'assignee_or_creator_and_pm':
            return isAssigneeOrCreator(actor, task);
        case 'not_assignee_and_pm':
            return !same(actor.userId, task.assigneeId);
        case 'anyone':
            return true;
    }
}
/**
 * Một lần đổi trạng thái cụ thể có hợp lệ không.
 *
 * Chạm vào Done — đi vào **hoặc** đi ra — là việc của rule Done: ai được đóng
 * thì mới được mở lại (người dùng chốt), nếu không thì PM vừa đóng xong một
 * việc, thành viên đã tự mở ra.
 */
export function canChangeTaskStatus(rules, actor, task, from, to) {
    if (from === 'done' || to === 'done')
        return canCompleteTask(rules, actor, task);
    return canMoveTask(rules, actor, task);
}
/**
 * Cột này nhận được một việc thuộc sprint `taskSprintId` không — APM-BOARD-a3: cột
 * ngoài sprint (`sprintId = null`) hợp lệ ở mọi board, cột của một sprint chỉ nhận
 * việc của **chính** sprint ấy. Cùng luật với `resolveStatusRow` của lệnh đổi trạng
 * thái; id so không phân biệt hoa thường.
 */
export function statusFitsSprint(statusSprintId, taskSprintId) {
    return (statusSprintId === null ||
        (taskSprintId !== null && statusSprintId.toLowerCase() === taskSprintId.toLowerCase()));
}
/**
 * Cột đích khi đưa một việc sang nhóm trạng thái `category` (nút "Bắt đầu làm" /
 * "Đóng việc", tick dòng checklist gắn việc): trong các cột việc ấy được vào
 * (`statusFitsSprint`), cột **đầu tiên theo vị trí** của đúng nhóm. `null` = dự
 * án không có cột nào như thế.
 *
 * Lấy cột đầu tiên của **cả bảng** là sai ở dự án Agile nhiều sprint: nó trúng cột
 * Xong của sprint khác và server từ chối "Cột này thuộc sprint khác".
 */
export function pickTargetStatus(statuses, taskSprintId, category) {
    return ([...statuses]
        .filter((status) => status.category === category && statusFitsSprint(status.sprintId, taskSprintId))
        // Ổn định: hai cột cùng vị trí (một của sprint, một chung) giữ thứ tự đầu vào.
        .sort((a, b) => a.position - b.position)[0] ?? null);
}
//# sourceMappingURL=task-status-policy.js.map